import SettingsIcon from '@listening/assets/icons/settings.svg?react';
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { NavArrowLeft } from 'iconoir-react';
import { useEffect } from 'react';
import { useViewportMediaQuery } from '@/hooks/use-viewport-media-query';
import { SettingsNav } from '@/components/web-fe/design-v2/settings-shell';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import * as m from '@/paraglide/messages';

/**
 * Layout route for every `/app/settings/*` pane: renders the heading and the
 * section rail once, so panes are just their own content.
 */
export const Route = createFileRoute('/app/_authenticated/_dashboard/settings')(
  {
    component: SettingsLayout,
    context: () => ({
      title: m.common_settings(),
      icon: SettingsIcon,
    }),
  },
);

const FIRST_PANE = '/app/settings/voice' as const;
const NARROW_QUERY = '(max-width: 1023px)';

function SettingsLayout() {
  const navigate = useNavigate();
  const isNarrow = useViewportMediaQuery(NARROW_QUERY, {
    mobile: true,
    desktop: false,
  });
  // Trailing slash and the locale prefix both vary, so match the tail.
  const isIndex = useRouterState({
    select: (state) => /\/app\/settings\/?$/.test(state.location.pathname),
  });

  // Wide screens always show a pane beside the rail. Done here rather than as a
  // `beforeLoad` redirect because only the client knows the viewport, and
  // checked against `matchMedia` because `isNarrow` starts at the server's
  // user-agent guess, which a narrow desktop window or a tablet can defeat.
  useEffect(() => {
    if (!isNarrow && isIndex && !window.matchMedia(NARROW_QUERY).matches)
      void navigate({ to: FIRST_PANE, replace: true });
  }, [isNarrow, isIndex, navigate]);

  const showRail = !isNarrow || isIndex;
  const showPane = !isIndex;

  return (
    <DashboardPage>
      {/* The heading doubles as the way back out of a pane, rather than
          repeating "Settings" again on a row of its own. */}
      <div className='flex items-center gap-1'>
        {isNarrow && showPane && (
          <button
            type='button'
            aria-label={m.common_back()}
            onClick={() => {
              void navigate({ to: '/app/settings' });
            }}
            className='-ml-1 flex size-8 shrink-0 items-center justify-center rounded-[var(--v2-button-radius)] text-[var(--v2-text-muted)] transition-colors hover:bg-black/[0.05] hover:text-[var(--v2-text-primary)] dark:hover:bg-white/10'
          >
            <NavArrowLeft className='size-5' />
          </button>
        )}
        <h1
          className='text-[22px] text-[var(--v2-text-primary)]'
          style={{ fontWeight: 'var(--v2-heading-weight)' }}
        >
          {m.common_settings()}
        </h1>
      </div>
      <div className='flex flex-col gap-[var(--v2-gap-6)] lg:flex-row'>
        {showRail && <SettingsNav />}
        {showPane && (
          <div className='flex min-w-0 flex-1 flex-col gap-[var(--v2-gap-4)]'>
            <Outlet />
          </div>
        )}
      </div>
    </DashboardPage>
  );
}
